import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Redirect } from 'react-router';
import { BrowserRouter, Route, RouteProps, Switch } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { useAsync } from 'react-use';

import AuthStore from '../store/AuthStore';
import SiteCommonTheme from '../style/SiteCommonTheme';
import Cover from '../components/Cover';
import Login from '../components/Login';
import PasswordReset from '../components/PasswordReset';
import { LessonStateContextProvider } from '../contexts/LessonStateContext';
import { StoreProvider } from '../contexts/StoreContext';
import Routes from './Routes';
import { MultiplePlayMovieContextProvider } from '../contexts/MultiplePlayMovieContext';

import * as Sentry from '@sentry/react';

// デバイス判定
const detectDeviceType = (): 'mobile' | 'tablet' | 'desktop' | 'unknown' => {
    if (typeof window === 'undefined') return 'unknown';
    
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/mobi|iphone|android.*mobile/.test(userAgent)) {
        return 'mobile';
    }
    if (/ipad|tablet|android(?!.*mobile)/.test(userAgent)) {
        return 'tablet';
    }
    if (/windows|macintosh|linux/.test(userAgent)) {
        return 'desktop';
    }
    
    return 'unknown';
};

const createGlobalStyles = (isMobile: boolean) => ({
    '@global': {
        '*': {
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            ...(isMobile ? {} : { touchAction: 'pan-x pan-y' }),
        },
        'html, body': {
            margin: 0,
            overscrollBehaviorY: 'none',
            background: '#F0F0F6',
        },
    },
});

const GlobalStyle = withStyles((theme) => {
    const deviceType = detectDeviceType();
    const isMobile = deviceType === 'mobile';
    
    return createGlobalStyles(isMobile);
})(() => null);

const PrivateRoute: React.FC<RouteProps> = observer((props) => {
    return AuthStore.isLoggedIn ? (
        <Route {...props} />
    ) : (
        <Route path='/'>
            <Redirect to='/login' />
        </Route>
    );
});

const AppContainer: React.FC = observer(() => {
    useAsync(async () => {
        await AuthStore.checkLogin();
    });

    // 古いSafariをサポートするのでJSのハックを入れる
    const fitViewHeight = () => {
        const tabletMinWidth = 744;
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        const rootDom = document.getElementById('root');

        const viewPortHeight = `${window.innerHeight}px`;
        htmlElement.style.height = viewPortHeight;

        if (window.innerWidth >= tabletMinWidth) {
            bodyElement.style.transform = 'scale(1)';
            bodyElement.style.width = '100dvw';
            bodyElement.style.height = viewPortHeight;
            if (rootDom) {
                rootDom.style.height = viewPortHeight;
            }
        } else {
            bodyElement.style.transform = 'scale(0.5)';
            bodyElement.style.transformOrigin = 'top left';
            bodyElement.style.height = '200dvh';
            bodyElement.style.width = '200dvw';
            if (rootDom) {
                rootDom.style.height = '100%';
            }
        }
    }

    window.addEventListener('resize', fitViewHeight);

    useEffect(() => {
        window.addEventListener('resize', fitViewHeight);
        fitViewHeight()

        return () => {
            window.removeEventListener('resize', fitViewHeight);
        };
    }, []);

    // Sentry用
    useEffect(() => {
        if (!AuthStore.isLoggedIn) {
            return;
        }
        Sentry?.setUser({
            id: AuthStore.tempoId,
            username: AuthStore.name ?? '-',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AuthStore.isLoggedIn]);

    return (
        <ThemeProvider theme={SiteCommonTheme}>
            <GlobalStyle />
            <BrowserRouter>
                {/** TODO: ローディング終了を検知してないので検知するようにする(多分、ログインが遅くなった場合は暫くログイン画面が出ることになる) */}
                <Switch>
                    <Route exact path='/cover'>
                        <Cover />
                    </Route>
                    <Route exact path='/login'>
                        {AuthStore.isLoggedIn ? <Redirect to='/' /> : <Login />}
                    </Route>
                    <Route exact path='/password/reset'>
                        <PasswordReset />
                    </Route>
                    <PrivateRoute>
                        <StoreProvider>
                            <LessonStateContextProvider>
                                <MultiplePlayMovieContextProvider>
                                    <Routes />
                                </MultiplePlayMovieContextProvider>
                            </LessonStateContextProvider>
                        </StoreProvider>
                    </PrivateRoute>
                </Switch>
            </BrowserRouter>
        </ThemeProvider>
    );
});

export default AppContainer;
